import { useCallback, useEffect, useState } from 'react';
import { commerceRequest } from '../lib/commerceApi';
import { ApiError } from '../lib/http';

export interface ApiQueryState<T> {
  readonly data: T | null;
  readonly error: string;
  readonly errorStatus: number | null;
  readonly loading: boolean;
}
export interface ApiQueryOptions {
  readonly pollMs?: number;
}
const empty = <T>(path: string | null): ApiQueryState<T> => ({
  data: null,
  error: '',
  errorStatus: null,
  loading: path !== null,
});

/** Data belongs to its path. Polling errors retain only data from that same path. */
export function useApiQuery<T>(path: string | null, { pollMs }: ApiQueryOptions = {}) {
  const [state, setState] = useState<ApiQueryState<T> & { path: string | null }>(() => ({
    ...empty<T>(path),
    path,
  }));
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (path === null) return;
    const controller = new AbortController();
    let generation = 0;
    const load = async () => {
      const attempt = ++generation;
      try {
        const data = await commerceRequest<T>(path, { signal: controller.signal });
        if (!controller.signal.aborted && attempt === generation)
          setState({ path, data, error: '', errorStatus: null, loading: false });
      } catch (reason: unknown) {
        if (controller.signal.aborted || attempt !== generation) return;
        setState((previous) => ({
          ...(previous.path === path ? previous : empty<T>(path)),
          path,
          error: reason instanceof Error ? reason.message : 'Request failed',
          errorStatus: reason instanceof ApiError ? reason.status : null,
          loading: false,
        }));
      }
    };
    void load();
    const timer = pollMs ? setInterval(() => void load(), pollMs) : undefined;
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [path, pollMs, reloadKey]);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);
  const current = state.path === path && path !== null ? state : empty<T>(path);
  return { ...current, reload };
}
